import * as React from "react";
import { IndividualEmploymentStatus } from "../../../../../../Wrap/Constants/Enums/Domain/IndividualEmploymentStatus";
import { InvestorType } from "../../../../../../Wrap/Constants/Enums/Domain/InvestorType";
import { ContentDatePicker, ACCEPT_DATE_FORMATS } from "../../../../../Shared/Entities/Constants/DateConstants";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";

export const quoteSteppersMap = {
    wrapperSelection: "Wrapper selection",
    paymentsIn: "Payments in",
    adviserCharges: "Adviser charges",
    investment: "Investment selection",
    drawdownDetails: "Drawdown details",
    incomePaymentDetails: "Income payment details",
    quoteReady: "Quote ready",
    withdrawal: "Withdrawals",
    SIPP: "SIPP",
    ISA: "ISA Stocks & Shares",
    PP: "Personal Portfolio",
};

export const IndividualEmploymentStatusOptions = [
    IndividualEmploymentStatus.Employed,
    IndividualEmploymentStatus.FullTimeEducation,
    IndividualEmploymentStatus.Other,
    IndividualEmploymentStatus.Retired,
    IndividualEmploymentStatus.SelfEmployed,
    IndividualEmploymentStatus.Unemployed,
    IndividualEmploymentStatus.Carer,
];

export const IndividualEmploymentStatusOptionsMap = {
    [IndividualEmploymentStatus.Employed]: "Employed",
    [IndividualEmploymentStatus.FullTimeEducation]: "Full time education",
    [IndividualEmploymentStatus.Other]: "Other",
    [IndividualEmploymentStatus.SelfEmployed]: "Self employed",
    [IndividualEmploymentStatus.Retired]: "Pensioner",
    [IndividualEmploymentStatus.Unemployed]: "Unemployed",
    [IndividualEmploymentStatus.Carer]: "Carer",
};

export const PersonalPortfolioTypesArray = [InvestorType.Single, InvestorType.Joint];

export const PersonalPortfolioTypesMap = {
    [InvestorType.Single]: "Single",
    [InvestorType.Joint]: "Joint",
};

export const FrequencyTypeArrays = [Frequency.Monthly, Frequency.Quarterly, Frequency.HalfYearly, Frequency.Yearly];

export const FrequencyArraysMap = {
    [Frequency.Monthly]: "Monthly",
    [Frequency.Quarterly]: "Quarterly",
    [Frequency.HalfYearly]: "Half yearly",
    [Frequency.Yearly]: "Yearly",
};

// Temporary until Drawdown details coded
export enum DrawdownType {
    Other = 0,
    RegularCrystallisation = 1,
    FlexiAccessRegular = 2,
}

export const datePickerOptions = {
    showYearDropdown: true,
    todayButton: ContentDatePicker.Today,
    isClearable: true,
    dateFormat: ContentDatePicker.DateFormat,
    acceptDateFormats: ACCEPT_DATE_FORMATS,
};

export const daysUntilFirstPaymentDate = 20;

export const quoteSippIncomePaymentDetailsLabels = {
    regularIncomePayment: "Regular Income Payment",
    taxableIncomeRequiredInAddition: "Is taxable income required in addition to the tax-free lump sum?",
    targetSpecificArrangements: "Do you wish to target specific arrangements?",
    takeDifferentInitialIncomePayment: "Do you wish to take a different initial income payment?",
    grossInitialIncomePayment: "Gross initial income payment",
    maximumInitialIncomePayment: "Maximum for initial income payment",
    maximumIncomePerPayment: "Maximum income per payment",
    maximumAvailablePerPayment: "Maximum available per payment",
    grossIncomePerPayment: "Gross income per payment",
    grossIncomePerPaymentFromPension: "Gross income per payment from the pension",
    incomeFrequency: "Income frequency",
    firstPaymentDate: "First payment date",
    endDate: "End date",
    untilNotified: "Until notified",
    enterADate: "Enter a date",
    calculateEstimatedPaye: "Calculate estimated PAYE",
    estimatedPayeTax: "Estimated PAYE tax",
    estimatedNetPayment: "Estimated net payment",
    taxCode: "Tax Code",
    taxBasis: "Basis",
    oneOffIncomePayment: "One-off income payment",
    takeOneOffIncomePayment: "Do you wish to take a one-off income payment?",
    maximumGrossOneOffIncomePayment: "Maximum gross one-off income payment",
    capped: "Capped",
    flexi: "Flexi",
    from: "From",
    amount: "Amount",
    type: "Type",
    member: "Member",
    gadMaximum: "GAD maximum",
    incomeTakenToDate: "Income taken to date",
    lastMandatoryReview: "Last mandatory review",
    maximumIncomePaymentForRemainderOfYear: "Maximum income payment for remainder of pension year",
    maximumIncomePaymentForFutureYears: "Maximum income payment for future pension years",
    incomePerPaymentForCurrentPensionYear: "Income per payment for current pension year",
    incomePerPaymentForFuturePensionYears: "Income per payment for future pension years",
    MaximumIncomePerPayment: "Maximum income payment per payment",
    incomePerPayment: "Income per payment",
    takeFullValue: "Take Full value",
    flexiArrangementsExhausted: "Please tick to confirm your client understands this and is happy to proceed",
    paymentMethod: "Payment method",
};

export const quoteSippIncomePaymentDetailsMessages = {
    grossInitialIncomePaymentRequired: "Please enter gross initial income payment",
    grossIncomePerPaymentRequired: "Please enter gross income per payment",
    grossIncomePerPaymentFromPensionRequired: "Please enter gross income per payment from the pension",
    incomeFrequencyRequired: "Please enter income frequency",
    firstPaymentDateRequired: "Please enter first payment date",
    enterADateRequired: "Please enter a date",
    oneOffIncomePaymentRequired: "Please enter one-off income payment",
    incomePerPaymentForCurrentPensionYearRequired: "Please enter income per payment for current pension year",
    incomePerPaymentForFuturePensionYearsRequired: "Please enter income per payment for future pension years",
    incomePerPaymentRequired: "Please enter income per payment",
    flexiArrangementsExhausted:
        "The income specified will exhaust one or more of your client's flexi access arrangements within a year",
    messagesGrossInitialIncomeOver: "Gross initial income payment must not be greater than maximum",
    grossIncomePerPaymentOver:
        "Please enter an income amount that is greater than zero and less than or equal to the maximum income per payment.",
    grossAvailablePerPaymentOver:
        "Please enter an income amount that is greater than zero and less than or equal to the maximum available per payment.",
    firstPaymentDateUnder15: "The date selected must be at least 15 working days from today",
    firstPaymentDateUnder10: "The date selected must be at least 10 working days from today",
    endDateUnder: "End date must be equal to or later than the first payment date",
    dateOver28: "Date must also be between 1-28 of any month",
    grossOneOffIncomePerPaymentOver:
        "One off income payment value cannot be greater than the Maximum gross one-off income payment value",
    targetSpecificArrangementsMessage: (
        <p className="warning-message">
            For purposes of illustration, the income is assumed to be taken proportionally across all crystallised
            arrangements
        </p>
    ),
    standardAlgorithmCappedMessage:
        "SL to provide content regarding the maximum gross annual income and the income paid to date",
    inheritedFundMessage:
        "SL to provide content around whether the inherited drawdowns should be targeted or excluded and they should consider the use of the standard algorithm or targeting arrangements",
};
